import { IconFile, IconMicrophone, IconX } from '@tabler/icons-react'
import type { AttachmentPreview } from './types'
import { formatBytes } from './utils'

type AttachmentCardProps = {
  attachment: AttachmentPreview
  onRemove: (id: string) => void
  onImageClick?: () => void
  onDocumentClick?: (attachment: AttachmentPreview) => void
}

export function AttachmentCard({ attachment, onRemove, onImageClick, onDocumentClick }: AttachmentCardProps) {
  const isImage = attachment.type === 'image' || attachment.type === 'sticker'
  const isVideo = attachment.type === 'video'
  const isAudio = attachment.type === 'audio' || attachment.type === 'ptt'
  const isDocument = attachment.type === 'document'
  const isPdf = isDocument && attachment.file.type === 'application/pdf'

  return (
    <div className='relative flex items-center gap-3 rounded-md border border-input/60 bg-muted/40 p-2 shadow-sm'>
      <button
        type='button'
        className='absolute right-1 top-1 rounded-full bg-background/80 p-1 text-muted-foreground transition hover:text-destructive'
        onClick={() => onRemove(attachment.id)}
      >
        <IconX size={14} />
      </button>
      <div className='flex h-14 w-14 items-center justify-center overflow-hidden rounded-md bg-background shadow-inner'>
        {isImage && attachment.previewUrl && (
          <img
            src={attachment.previewUrl}
            alt={attachment.file.name}
            className='h-full w-full object-cover cursor-pointer hover:opacity-90 transition-opacity'
            onClick={onImageClick}
          />
        )}
        {isVideo && attachment.previewUrl && (
          <video
            src={attachment.previewUrl}
            className='h-full w-full object-cover cursor-pointer hover:opacity-90 transition-opacity'
            muted
            playsInline
            onClick={onImageClick}
          />
        )}
        {isAudio && (
          <IconMicrophone size={18} className='text-muted-foreground' />
        )}
        {isDocument && (
          <div
            className='flex h-full w-full cursor-pointer items-center justify-center hover:bg-muted/60 transition-colors'
            onClick={() => (isPdf ? onImageClick?.() : onDocumentClick?.(attachment))}
          >
            <IconFile size={18} className='text-muted-foreground' />
          </div>
        )}
        {!isImage && !isVideo && !isAudio && !isDocument && (
          <IconFile size={18} className='text-muted-foreground' />
        )}
      </div>
      <div className='min-w-0 flex-1'>
        <p className='truncate text-sm font-medium'>{attachment.file.name}</p>
        <p className='text-xs text-muted-foreground'>
          {attachment.type} • {formatBytes(attachment.size)}
        </p>
      </div>
    </div>
  )
}
